import { ComponentProps } from 'react'

import { F0Alert } from '@factorialco/f0-react'
import {
  PerformanceProcessAgreementStatusAlertEsignaturesFragment,
  PerformanceProcessAgreementStatusAlertFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

interface UseStatusAlertParams {
  agreement: PerformanceProcessAgreementStatusAlertFragment | null
  esignatures: PerformanceProcessAgreementStatusAlertEsignaturesFragment[]
}

export const useStatusAlert = ({
  agreement,
  esignatures,
}: UseStatusAlertParams): ComponentProps<typeof F0Alert> | null => {
  const { access } = useCurrentSession()

  if (!agreement) return null

  if (agreement.status === 'signed') {
    const agreementDocument = agreement.agreementDocuments[0]
    const document = agreementDocument?.document
    const signatureStatus = agreementDocument?.signatureStatus

    if (!signatureStatus) return null

    if (signatureStatus === 'pending' || signatureStatus === 'partially_signed') {
      return {
        title: i18n.t('performance_process.agreements.agreement.status_alert.pending.title'),
        description: i18n.t(
          'performance_process.agreements.agreement.status_alert.pending.description'
        ),
        variant: 'warning',
      }
    }

    if (signatureStatus === 'declined') {
      const declinedSignee = esignatures?.find(
        (signature) => signature.status === 'declined'
      )?.signee

      return {
        title: i18n.t('performance_process.agreements.agreement.status_alert.declined.title'),
        description: i18n.t(
          'performance_process.agreements.agreement.status_alert.declined.description',
          { name: declinedSignee?.firstName || i18n.t('common.unknown') }
        ),
        variant: 'critical',
      }
    }

    if (signatureStatus === 'expired') {
      return {
        title: i18n.t('performance_process.agreements.agreement.status_alert.expired.title'),
        description: i18n.t(
          'performance_process.agreements.agreement.status_alert.expired.description'
        ),
        variant: 'critical',
      }
    }

    if (document?.deletedAt && signatureStatus !== 'completed') {
      const date = i18nDateToString(stringToDate(document.deletedAt, 'backend_datetime'), 'ui')

      return {
        title: i18n.t('performance_process.agreements.agreement.status_alert.deleted.title'),
        description: i18n.t(
          'performance_process.agreements.agreement.status_alert.deleted.description',
          { date }
        ),
        variant: 'critical',
      }
    }
  } else if (agreement.locked) {
    const firstSignedByMe =
      (agreement.managerSignedAt && agreement.signer?.id == access.id) ||
      (agreement.targetSignedAt && agreement.target?.access?.id == access.id)

    if (firstSignedByMe) return null

    return {
      title: i18n.t('performance_process.agreements.agreement.status_alert.locked.title'),
      description: i18n.t(
        'performance_process.agreements.agreement.status_alert.locked.description',
        { name: agreement.signer?.firstName || agreement.target?.access?.firstName }
      ),
      variant: 'info',
    }
  }

  return null
}
